/**
 * SOX cadence ceiling — client-side mirror of functions/src/shared/soxCeiling.ts.
 *
 * The backend remains the source of truth and re-validates every PATCH and
 * bulk-extend; this client copy exists only so the admin dashboard's date
 * picker can set its `max` attribute (preventing the user from even SELECTING
 * an invalid deadline) and so labels can show the ceiling without an extra
 * round-trip.
 *
 * Duplicated rather than imported because the webapp build doesn't share
 * code with the Functions TypeScript project. If you change the logic here,
 * change it in functions/src/shared/soxCeiling.ts too.
 */

/**
 * Returns the SOX-ceiling Date for a cycle that started at `stage1StartedAt`:
 * the last instant of the trigger month in Berlin local time, expressed as
 * an absolute UTC Date.
 */
export function getMaxAllowedDeadline(stage1StartedAt: Date): Date {
  const berlinParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year:  "numeric",
    month: "2-digit",
  }).formatToParts(stage1StartedAt);
  const year  = parseInt(berlinParts.find((p) => p.type === "year")!.value,  10);
  const month = parseInt(berlinParts.find((p) => p.type === "month")!.value, 10);
  return lastInstantOfMonthInBerlin(year, month);
}

function lastInstantOfMonthInBerlin(year: number, month1to12: number): Date {
  const lastDay = new Date(Date.UTC(year, month1to12, 0)).getUTCDate();
  const nextMonthYear  = month1to12 === 12 ? year + 1 : year;
  const nextMonth1to12 = month1to12 === 12 ? 1 : month1to12 + 1;

  for (const utcHour of [22, 23]) {
    const candidate = new Date(Date.UTC(year, month1to12 - 1, lastDay, utcHour, 0, 0, 0));
    const berlinParts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Berlin",
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(candidate);
    const get = (t: string) => parseInt(berlinParts.find((p) => p.type === t)?.value ?? "NaN", 10);
    if (
      get("year")   === nextMonthYear &&
      get("month")  === nextMonth1to12 &&
      get("day")    === 1 &&
      get("hour")   === 0 &&
      get("minute") === 0
    ) {
      return new Date(candidate.getTime() - 1);
    }
  }
  return new Date(Date.UTC(year, month1to12 - 1, lastDay, 22, 59, 59, 999));
}
